import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { MessageCircle, Bug, Lightbulb, HelpCircle, Send, Loader2 } from 'lucide-react';
import api from '../lib/api';

const CONTACT_TYPES = [
  { id: 'problem', icon: Bug, color: 'text-red-500' },
  { id: 'suggestion', icon: Lightbulb, color: 'text-amber-500' },
  { id: 'question', icon: HelpCircle, color: 'text-blue-500' },
];

export default function ContactSupportModal({ open, onOpenChange }) {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  
  const [contactType, setContactType] = useState('problem');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) {
      toast.error(t('message_required') || 'Le message est requis');
      return;
    }
    
    if (message.trim().length < 10) {
      toast.error(t('message_too_short') || 'Le message doit contenir au moins 10 caractères');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await api.post('/contact/send', {
        contact_type: contactType,
        subject: subject.trim() || undefined,
        message: message.trim(),
        language: language,
        user_name: user?.name,
        user_email: user?.email
      });
      
      toast.success(t('contact_sent_success') || 'Message envoyé ! Nous vous répondrons rapidement.');
      
      // Reset form
      setContactType('problem');
      setSubject('');
      setMessage('');
      onOpenChange(false);
      
    } catch (error) {
      console.error('Failed to send contact:', error);
      toast.error(error.response?.data?.detail || t('contact_send_error') || 'Erreur lors de l\'envoi');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTypeLabel = (typeId) => {
    const labels = {
      problem: { fr: 'Signaler un problème', en: 'Report a problem', pt: 'Relatar um problema' },
      suggestion: { fr: 'Suggérer une amélioration', en: 'Suggest an improvement', pt: 'Sugerir uma melhoria' },
      question: { fr: 'Poser une question', en: 'Ask a question', pt: 'Fazer uma pergunta' }
    };
    return labels[typeId]?.[language] || labels[typeId]?.fr;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-primary" />
            {t('contact_support') || 'Contacter le support'}
          </DialogTitle>
          <DialogDescription>
            {t('contact_support_desc') || 'Envoyez-nous un message, nous vous répondrons rapidement.'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          {/* Contact Type Selection */}
          <div className="space-y-2">
            <Label>{t('contact_type') || 'Type de demande'}</Label>
            <div className="grid grid-cols-3 gap-2">
              {CONTACT_TYPES.map(({ id, icon: Icon, color }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setContactType(id)}
                  className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${
                    contactType === id 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid={`contact-type-${id}`}
                >
                  <Icon className={`w-5 h-5 ${contactType === id ? 'text-primary' : color}`} />
                  <span className="text-xs font-medium text-center">
                    {id === 'problem' && (t('problem') || 'Problème')}
                    {id === 'suggestion' && (t('suggestion') || 'Suggestion')}
                    {id === 'question' && (t('question_label') || 'Question')}
                  </span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Subject (optional) */}
          <div className="space-y-2">
            <Label htmlFor="contact-subject">
              {t('subject')} <span className="text-muted-foreground text-xs">({t('optional')})</span>
            </Label>
            <Input
              id="contact-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t('contact_subject_placeholder') || 'Ex: Problème de connexion'}
              maxLength={100}
              data-testid="contact-subject-input"
            />
          </div>
          
          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="contact-message">
              {t('message')} <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="contact-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('contact_message_placeholder') || 'Décrivez votre demande en détail...'}
              rows={5}
              maxLength={2000}
              required
              data-testid="contact-message-input"
            />
            <p className="text-xs text-muted-foreground text-right">
              {message.length}/2000
            </p>
          </div>
          
          {/* User info preview */}
          <div className="bg-muted/50 rounded-lg p-3 text-sm">
            <p className="text-muted-foreground">
              {t('contact_sent_as') || 'Envoyé en tant que'}: <span className="font-medium text-foreground">{user?.name}</span>
            </p>
            <p className="text-muted-foreground">
              {t('reply_to') || 'Réponse à'}: <span className="font-medium text-foreground">{user?.email}</span>
            </p>
          </div>
          
          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button 
              type="button" 
              variant="outline" 
              className="flex-1"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('cancel')}
            </Button>
            <Button 
              type="submit" 
              className="flex-1"
              disabled={isSubmitting || !message.trim()}
              data-testid="contact-submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('sending') || 'Envoi...'}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {t('send')}
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
